import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { fetchInspectionSettings, clearInspectionSettings, createInspection, updateInspection, getInspectionScheduleStatus } from 'store/actions/inspect';
import { adoptInspectionResource } from 'helpers/inspectionResourceHelper';
import { fetchSettingsFields, fetchSettingsOperators } from 'store/actions/slateViews';
import { fetchReset } from 'store/actions/entities';
import { entityNames } from 'config/constants';
import RaisedButton from 'material-ui/RaisedButton';
import IntoFooter from 'components/Footer/IntoFooter';
import BlueButton from 'components/Shared/BlueButton/BlueButton';
import BasicDialog from 'components/Dialogs/BasicDialog';
import { SlateDialog } from 'components/Shared/SlateDialog';
import { describeRecurrence } from './sections/recurrence/describeRecurrence';
import { graceCeilingError } from 'helpers/recurrenceLimits';
import palette from 'theme/palette';
import InspectSettingsNav from './InspectSettingsNav';
import InspectionData from './sections/InspectionData';
import PassFailCriteria from './sections/PassFailCriteria';
import AssetType from './sections/AssetType';
import Recurrence from './sections/Recurrence';
import ActiveInactive from './sections/ActiveInactive';
import NeglectWarnings from './sections/NeglectWarnings';
import Columns from './sections/Columns';
import { CUSTOM_COLUMN_LISTS } from './sections/columns/inspectColumns';
import Notifications from './sections/Notifications/Notifications';
import Preloader from 'components/Preloader/Preloader';
import './style.css';

var SECTION_COMPONENTS = {
  inspectionData: InspectionData,
  passFailCriteria: PassFailCriteria,
  assetType: AssetType,
  recurrence: Recurrence,
  activeInactive: ActiveInactive,
  neglectWarnings: NeglectWarnings,
  columns: Columns,
  notifications: Notifications,
};

var SECTION_SLUGS = {
  inspectionData: 'inspection-data',
  passFailCriteria: 'pass-fail-criteria',
  assetType: 'asset-type',
  recurrence: 'recurrence',
  activeInactive: 'active-inactive',
  neglectWarnings: 'neglect-warnings',
  columns: 'columns',
  notifications: 'notifications',
};

var SLUG_TO_SECTION = {};
Object.keys(SECTION_SLUGS).forEach(function (key) {
  SLUG_TO_SECTION[SECTION_SLUGS[key]] = key;
});

var DEFAULT_SECTION = 'inspectionData';

var ERROR_KEYS_BY_SECTION = {
  inspectionData: ['inspectionRecordsFieldPathHash'],
  passFailCriteria: ['passOrFail'],
  assetType: ['assetTypeFieldPathHash', 'assetTypeValue'],
  recurrence: [
    'recurrenceType',
    'manualRecurrenceDateFieldPathHash',
    'scheduledRecurrenceFrequency',
    'scheduledRecurrenceInterval',
    'scheduledRecurrenceDaysOfWeek',
    'scheduledRecurrenceHoursOfDay',
    'scheduledRecurrenceDaysOfMonth',
    'scheduledRecurrenceStartDate',
    'scheduledRecurrenceTimeZone',
    'scheduledFirstDueAnchor',
    'scheduledFirstDueDate',
    'scheduledFirstDueFieldPathHash',
    'scheduledFirstDueOffsetInterval',
    'scheduledFirstDueOffsetUnit',
    'scheduledCompletionGraceInterval',
    'scheduledCompletionGraceUnit',
  ],
  activeInactive: ['isActiveFieldPathHash'],
  neglectWarnings: ['neglectWarningsPeriodInterval', 'neglectWarningsPeriodUnit'],
};

var CUSTOM_COLUMN_LIST_KEYS = Object.keys(CUSTOM_COLUMN_LISTS).map(function (tab) { return CUSTOM_COLUMN_LISTS[tab]; });
var ERROR_PREFIXES_BY_SECTION = { columns: CUSTOM_COLUMN_LIST_KEYS };

var SECTION_ORDER = [
  'inspectionData',
  'assetType',
  'passFailCriteria',
  'recurrence',
  'activeInactive',
  'neglectWarnings',
  'columns',
  'notifications',
];

export function pickSectionErrors(dataErrors, sectionKey) {
  if (!dataErrors) return null;
  var keys = ERROR_KEYS_BY_SECTION[sectionKey] || [];
  var prefixes = ERROR_PREFIXES_BY_SECTION[sectionKey] || [];
  var result = null;
  Object.keys(dataErrors).forEach(function (errorKey) {
    if (!dataErrors[errorKey]) return;
    var matches = keys.indexOf(errorKey) !== -1 || prefixes.some(function (prefix) {
      return errorKey.indexOf(prefix + '[') === 0;
    });
    if (!matches) return;
    if (!result) result = {};
    result[errorKey] = dataErrors[errorKey];
  });
  return result;
}

function firstSectionWithErrors(dataErrors) {
  if (!dataErrors) return null;
  for (var i = 0; i < SECTION_ORDER.length; i++) {
    if (pickSectionErrors(dataErrors, SECTION_ORDER[i])) return SECTION_ORDER[i];
  }
  return null;
}

var SESSION_KEY = 'inspectSettingsConfig';

var DEFAULT_CONFIG = {
  name: '',
  recordsFieldKey: '',
  recordsFieldTitle: '',
  passFailCriteria: { mode: null, rules: [] },
  assetTypeFieldKey: null,
  assetTypeFieldTitle: '',
  assetTypeValue: '',
  recurrence: { mode: null },
  activeFieldKey: null,
  activeFieldTitle: '',
  neglectWarnings: null,
  historyCustomColumns: [],
  scheduledCustomColumns: [],
  notifications: { onPass: [], onFail: [], scheduledBefore: [], scheduledAfter: [], onNeglect: [], summary: [] },
};

export function clearInspectSettingsSessionStorage() {
  try { sessionStorage.removeItem(SESSION_KEY); } catch (e) { /* ignore */ }
}

export class InspectSettings extends Component {
  constructor(props) {
    super(props);
    var inspectionUUID = props.match.params.inspectionUUID;
    var sectionSlug = props.match.params.section;
    this.isNewMode = !inspectionUUID;
    var initialSection = (sectionSlug && SLUG_TO_SECTION[sectionSlug]) || DEFAULT_SECTION;

    // Restore config from sessionStorage if available
    var restoredConfig = null;
    try {
      var saved = sessionStorage.getItem(SESSION_KEY);
      if (saved) {
        var parsed = JSON.parse(saved);
        // Only restore if the mode (new vs edit UUID) matches
        var savedIsNew = !parsed._inspectionUUID;
        var currentIsNew = !inspectionUUID;
        if (savedIsNew === currentIsNew && parsed._inspectionUUID === (inspectionUUID || '')) {
          restoredConfig = parsed.config;
        }
      }
    } catch (e) { /* ignore */ }

    this.state = {
      activeSection: initialSection,
      config: restoredConfig || Object.assign({}, DEFAULT_CONFIG),
      isDirty: !!restoredConfig,
      isLeaveDialogOpen: false,
      pendingLeavePath: null,
      dataErrors: null,
      scheduleStatus: null,
      recomputeExistingDueDates: false,
      confirmSummary: null,
      recurrenceDirty: false,
      isConflictDialogOpen: false,
    };
  }

  _inspectBasePath = function () {
    var accountUUID = this.props.currentAccount && this.props.currentAccount.uuid;
    return '/accounts/' + (accountUUID || '') + '/inspect';
  }.bind(this);

  // How many of this inspection's assets already carry a due date. Advisory:
  // a failed or absent count simply hides the keep/recompute banner.
  _loadScheduleStatus = function (inspectionUUID) {
    var self = this;
    var accountUUID = this.props.currentAccount && this.props.currentAccount.uuid;
    if (!accountUUID || !inspectionUUID || this._scheduleStatusFetchStarted) return;
    this._scheduleStatusFetchStarted = true;
    getInspectionScheduleStatus(accountUUID, inspectionUUID).then(function (status) {
      if (status && !self._isUnmounted) self.setState({ scheduleStatus: status });
    });
  }.bind(this);

  handleRecomputeChange = function (value) {
    // Not a config change: the flag is transient, so it does not mark the form
    // dirty and is not persisted to sessionStorage.
    this.setState({ recomputeExistingDueDates: !!value, recurrenceDirty: true });
  }.bind(this);

  UNSAFE_componentWillMount() {
    var inspectionUUID = this.props.match.params.inspectionUUID;
    // Only start the fetch once the account is known. On a hard refresh the
    // account is restored asynchronously; firing before it resolves makes the
    // thunk bail silently and currentInspection never gets populated (breadcrumb
    // then falls back to the generic route title). We retry in componentDidUpdate
    // once currentAccount becomes available.
    if (inspectionUUID && this.props.currentAccount) {
      this._inspectionFetchStarted = true;
      this.props.fetchInspectionSettings(inspectionUUID);
      this._loadScheduleStatus(inspectionUUID);
    }
    this.props.fetchSettingsFields();
    // Clear any stale save errors from a previous session
    this.props.fetchReset(entityNames.UPDATE_INSPECTION);
  }

  componentDidMount() {
    this.unblockHistory = this.props.history.block(function (location) {
      if (!this.state.isDirty) return true;
      // Allow navigation within inspect settings sections
      var basePath = this.isNewMode
        ? this._inspectBasePath() + '/new'
        : this._inspectBasePath() + '/' + this.props.match.params.inspectionUUID + '/configuration';
      if (location.pathname.indexOf(basePath) === 0) return true;
      this.setState({
        isLeaveDialogOpen: true,
        pendingLeavePath: location.pathname,
      });
      return false;
    }.bind(this));
  }

  componentDidUpdate(prevProps, prevState) {
    var settings = this.props.inspectionSettings;
    var prevSettings = prevProps.inspectionSettings;
    if (settings && settings !== prevSettings) {
      this.setState({ config: Object.assign({}, this.state.config, settings) });
      // Pre-fetch operators for existing pass/fail rules so the operator/value
      // inputs render on load (mirrors FormSlateViewSettings.fetchOperators).
      var rules = settings.passFailCriteria && settings.passFailCriteria.rules;
      if (rules && rules.length) {
        for (var i = 0; i < rules.length; i++) {
          var fieldSUUID = rules[i].fieldSUUID;
          if (fieldSUUID) this.props.fetchSettingsOperators(fieldSUUID, true);
        }
      }
    }
    // Re-resolve field titles when settingsFields loads after a GET response.
    // Only merge title-derived keys so user edits made before this moment are
    // preserved.
    if (
      this.props.settingsFields
      && !prevProps.settingsFields
      && this.props.inspectionSettingsRaw
    ) {
      var reAdopted = adoptInspectionResource(
        this.props.inspectionSettingsRaw,
        this.props.settingsFields
      );
      var titleUpdate = {};
      if (reAdopted.assetTypeFieldTitle) titleUpdate.assetTypeFieldTitle = reAdopted.assetTypeFieldTitle;
      if (reAdopted.activeFieldTitle) titleUpdate.activeFieldTitle = reAdopted.activeFieldTitle;
      var mergedRecurrence = null;
      if (
        reAdopted.recurrence
        && (reAdopted.recurrence.fieldTitle || reAdopted.recurrence.firstDueFieldTitle)
      ) {
        mergedRecurrence = Object.assign({}, this.state.config.recurrence || {});
        if (reAdopted.recurrence.fieldTitle) {
          mergedRecurrence.fieldTitle = reAdopted.recurrence.fieldTitle;
        }
        if (reAdopted.recurrence.firstDueFieldTitle) {
          mergedRecurrence.firstDueFieldTitle = reAdopted.recurrence.firstDueFieldTitle;
        }
      }
      var nextConfig = Object.assign({}, this.state.config, titleUpdate);
      if (mergedRecurrence) nextConfig.recurrence = mergedRecurrence;
      this.setState({ config: nextConfig });
    }
    // Retry field fetch if account became available after mount
    if (this.props.currentAccount && !this.props.settingsFields && !this._fieldsFetchStarted) {
      this._fieldsFetchStarted = true;
      this.props.fetchSettingsFields();
    }
    // Retry inspection fetch if account became available after mount
    var inspectionUUIDForRetry = this.props.match.params.inspectionUUID;
    if (
      inspectionUUIDForRetry &&
      this.props.currentAccount &&
      !this._inspectionFetchStarted
    ) {
      this._inspectionFetchStarted = true;
      this.props.fetchInspectionSettings(inspectionUUIDForRetry);
      this._loadScheduleStatus(inspectionUUIDForRetry);
    }
    // Sync active section when URL changes (browser back/forward)
    var prevSection = prevProps.match.params.section;
    var currentSection = this.props.match.params.section;
    if (prevSection !== currentSection) {
      var newSection = (currentSection && SLUG_TO_SECTION[currentSection]) || DEFAULT_SECTION;
      if (newSection !== this.state.activeSection) {
        this.setState({ activeSection: newSection });
      }
    }
    // Sync API dataErrors into local state and jump to the first section with errors
    var saveEntity = this.props.saveEntity;
    var prevSaveEntity = prevProps.saveEntity;
    var currErrors = saveEntity && saveEntity.hasErrors && saveEntity.hasErrors.dataErrors;
    var prevErrors = prevSaveEntity && prevSaveEntity.hasErrors && prevSaveEntity.hasErrors.dataErrors;
    if (currErrors && currErrors !== prevErrors) {
      var activeSectionErrors = pickSectionErrors(currErrors, this.state.activeSection);
      var firstErrorSection = firstSectionWithErrors(currErrors);
      var nextState = { dataErrors: currErrors };
      this.setState(nextState, function () {
        if (!activeSectionErrors && firstErrorSection && firstErrorSection !== this.state.activeSection) {
          this.handleSectionClick(firstErrorSection);
        }
      }.bind(this));
    }
    // Persist config to sessionStorage when it changes
    if (prevState.config !== this.state.config) {
      try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({
          _inspectionUUID: this.props.match.params.inspectionUUID || '',
          config: this.state.config,
        }));
      } catch (e) { /* ignore */ }
    }
  }

  componentWillUnmount() {
    this._isUnmounted = true;
    if (this.unblockHistory) this.unblockHistory();
    this.props.clearInspectionSettings();
  }

  handleSectionClick = function (sectionKey) {
    this.setState({ activeSection: sectionKey });
    var slug = SECTION_SLUGS[sectionKey];
    var params = this.props.match.params;
    var basePath = this.isNewMode
      ? this._inspectBasePath() + '/new'
      : this._inspectBasePath() + '/' + params.inspectionUUID + '/configuration';
    var newPath = sectionKey === DEFAULT_SECTION
      ? basePath
      : basePath + '/' + slug;
    this.props.history.replace(newPath);
  }.bind(this);

  handleConfigChange = function (key, value) {
    var config = Object.assign({}, this.state.config);
    config[key] = value;
    // Editing a column list drops that list's server errors (they index into the old array).
    var nextErrors = this.state.dataErrors;
    if (CUSTOM_COLUMN_LIST_KEYS.indexOf(key) !== -1 && nextErrors) {
      var remaining = {};
      var kept = 0;
      Object.keys(nextErrors).forEach(function (errorKey) {
        if (errorKey.indexOf(key + '[') === 0) return;
        remaining[errorKey] = nextErrors[errorKey];
        kept += 1;
      });
      nextErrors = kept ? remaining : null;
    }
    // A recurrence edit (or an On Save choice) is confirmed at save time with a plain-English
    // restatement. Tracked as a flag rather than diffed against the loaded settings, because
    // componentDidUpdate re-resolves field titles into config.recurrence alone.
    this.setState({ config: config, dataErrors: nextErrors, isDirty: true, recurrenceDirty: this.state.recurrenceDirty || key === 'recurrence' });
  }.bind(this);

  generateName = function (config) {
    var records = config.recordsFieldTitle;
    var asset = config.assetTypeFieldTitle;
    if (records && asset) return records + ' by ' + asset;
    if (records) return records;
    if (asset) return asset;
    return '';
  }.bind(this);

  handleRecordsFieldChange = function (suuid, title) {
    var config = Object.assign({}, this.state.config);
    config.recordsFieldKey = suuid;
    config.recordsFieldTitle = title;
    config.name = this.generateName(config);
    this.setState({ config: config, isDirty: true });
  }.bind(this);

  handleAssetTypeFieldChange = function (suuid, title) {
    var config = Object.assign({}, this.state.config);
    config.assetTypeFieldKey = suuid;
    config.assetTypeFieldTitle = title;
    config.name = this.generateName(config);
    this.setState({ config: config, isDirty: true });
  }.bind(this);

  handleAssetTypeValueChange = function (value) {
    var config = Object.assign({}, this.state.config);
    config.assetTypeValue = value;
    this.setState({ config: config, isDirty: true });
  }.bind(this);

  handleActiveFieldChange = function (suuid, title) {
    var config = Object.assign({}, this.state.config);
    config.activeFieldKey = suuid;
    config.activeFieldTitle = title;
    this.setState({ config: config, isDirty: true });
  }.bind(this);

  handleSave = function () {
    var saveEntity = this.props.saveEntity;
    if (saveEntity && saveEntity.isFetching) return;
    var state = this.state;
    // The one validator rule the client can settle for itself before the Confirm
    // dialog: a completion window past the ceiling is shown on the field now,
    // not after a round trip. Everything else still comes back as API dataErrors.
    // Other fields' API errors stay on screen either way: the pre-check only
    // owns its own key, set here or dropped once the window fits.
    var dataErrors = Object.assign({}, state.dataErrors);
    var ceilingError = graceCeilingError(state.config.recurrence, this.props.t);
    if (ceilingError) {
      dataErrors.scheduledCompletionGraceInterval = ceilingError;
      this.setState({ dataErrors: dataErrors });
      var errorSection = firstSectionWithErrors({ scheduledCompletionGraceInterval: ceilingError });
      if (errorSection && errorSection !== state.activeSection) this.handleSectionClick(errorSection);
      return;
    }
    delete dataErrors.scheduledCompletionGraceInterval;
    if (state.recurrenceDirty) {
      this.setState({
        dataErrors: dataErrors,
        confirmSummary: describeRecurrence(state.config.recurrence, state.scheduleStatus, state.recomputeExistingDueDates, this.props.t),
      });
      return;
    }
    this.setState({ dataErrors: dataErrors });
    this.performSave();
  }.bind(this);

  handleConfirmSave = function () {
    this.setState({ confirmSummary: null });
    this.performSave();
  }.bind(this);

  handleConfirmBack = function () {
    this.setState({ confirmSummary: null });
  }.bind(this);

  performSave = function () {
    var self = this;
    var savePromise = this.isNewMode
      ? this.props.createInspection(this.state.config)
      : this.props.updateInspection(
        this.props.match.params.inspectionUUID,
        this.state.config,
        this.state.recomputeExistingDueDates
      );
    savePromise.then(function () {
      clearInspectSettingsSessionStorage();
      if (self.unblockHistory) self.unblockHistory();
      self.setState({ isDirty: false }, function () {
        self.props.history.push(self._inspectBasePath());
      });
    }, function (err) {
      // Other error state is already dispatched by the thunk; stay on page.
      // A 409 means nothing was saved: ask whether to reload or keep editing.
      if (err && err.response && err.response.status === 409) {
        self.setState({ isConflictDialogOpen: true });
      }
    });
  }.bind(this);

  handleConflictCancel = function () {
    this.setState({ isConflictDialogOpen: false });
  }.bind(this);

  // Edit mode only: throw away the unsaved edits and load the latest version.
  handleConflictReload = function () {
    clearInspectSettingsSessionStorage();
    // Reset config first: the componentDidUpdate merge of the fetched settings
    // keeps any key the response lacks, so stale edits would otherwise survive.
    this.setState({
      config: Object.assign({}, DEFAULT_CONFIG),
      isDirty: false,
      dataErrors: null,
      recurrenceDirty: false,
      recomputeExistingDueDates: false,
      confirmSummary: null,
      isConflictDialogOpen: false,
    });
    this.props.fetchReset(entityNames.UPDATE_INSPECTION);
    this._scheduleStatusFetchStarted = false;
    var inspectionUUID = this.props.match.params.inspectionUUID;
    if (this.props.currentAccount) {
      this._inspectionFetchStarted = true;
      this.props.fetchInspectionSettings(inspectionUUID);
      this._loadScheduleStatus(inspectionUUID);
    } else {
      // The account retry in componentDidUpdate does the fetch once it is known.
      this._inspectionFetchStarted = false;
    }
  }.bind(this);

  handleCancel = function () {
    if (this.state.isDirty) {
      this.setState({ isLeaveDialogOpen: true, pendingLeavePath: this._inspectBasePath() });
    } else {
      clearInspectSettingsSessionStorage();
      if (this.unblockHistory) this.unblockHistory();
      this.props.history.push(this._inspectBasePath());
    }
  }.bind(this);

  handleLeaveConfirm = function () {
    var path = this.state.pendingLeavePath;
    clearInspectSettingsSessionStorage();
    if (this.unblockHistory) this.unblockHistory();
    this.setState({ isLeaveDialogOpen: false, isDirty: false, pendingLeavePath: null }, function () {
      this.props.history.push(path || this._inspectBasePath());
    }.bind(this));
  }.bind(this);

  handleLeaveCancel = function () {
    this.setState({ isLeaveDialogOpen: false, pendingLeavePath: null });
  }.bind(this);

  getSectionStatus = function () {
    var config = this.state.config;
    var status = {};

    status.inspectionData = {
      complete: !!config.recordsFieldKey,
      summary: config.recordsFieldKey ? (config.recordsFieldTitle || config.recordsFieldKey) : '',
    };
    var completeRuleCount = 0;
    if (config.passFailCriteria && config.passFailCriteria.rules) {
      var noValueOps = ['existsOnSlate', 'doesntExistOnSlate', 'hasValue', 'doesntHaveValue'];
      for (var i = 0; i < config.passFailCriteria.rules.length; i++) {
        var rule = config.passFailCriteria.rules[i];
        if (!rule.fieldSUUID || !rule.operation) continue;
        if (noValueOps.indexOf(rule.operation) === -1 && !rule.value) continue;
        completeRuleCount++;
      }
    }
    var hasPassFail = config.passFailCriteria && config.passFailCriteria.mode && completeRuleCount > 0;
    status.passFailCriteria = {
      complete: hasPassFail,
      summary: hasPassFail
        ? completeRuleCount + ' ' + config.passFailCriteria.mode + ' ' + (completeRuleCount === 1 ? 'rule' : 'rules')
        : '',
    };
    var assetTypeSummary = '';
    if (config.assetTypeFieldKey) {
      var assetFieldLabel = config.assetTypeFieldTitle || config.assetTypeFieldKey || '';
      if (config.assetTypeValue) {
        assetTypeSummary = assetFieldLabel + ' = ' + config.assetTypeValue;
      } else {
        assetTypeSummary = assetFieldLabel;
      }
    }
    status.assetType = {
      complete: !!config.assetTypeFieldKey,
      summary: assetTypeSummary,
    };
    var SINGULAR_UNITS = { hours: 'hour', days: 'day', weeks: 'week', months: 'month', years: 'year' };
    var FREQUENCY_UNITS = { hourly: 'hours', daily: 'days', weekly: 'weeks', monthly: 'months', yearly: 'years' };
    var formatPeriod = function (every, unit) {
      if (every === 1) return 'every ' + SINGULAR_UNITS[unit];
      return 'every ' + every + ' ' + unit;
    };
    var recMode = config.recurrence ? config.recurrence.mode : null;
    var recSummary = '';
    if (recMode === 'manual') {
      recSummary = config.recurrence.fieldTitle || config.recurrence.fieldKey || 'manual';
    } else if (recMode === 'schedule') {
      recSummary = formatPeriod(
        config.recurrence.interval || 1,
        FREQUENCY_UNITS[config.recurrence.frequency] || 'days'
      );
    }
    status.recurrence = {
      complete: !!recMode && recMode !== 'none',
      summary: recSummary,
    };
    status.activeInactive = {
      complete: !!config.activeFieldKey,
      summary: config.activeFieldTitle || config.activeFieldKey || '',
    };
    var neglectSummary = '';
    if (config.neglectWarnings && config.neglectWarnings.every) {
      var nEvery = config.neglectWarnings.every;
      var nUnit = config.neglectWarnings.unit || 'days';
      neglectSummary = 'after ' + nEvery + ' ' + (nEvery === 1 ? SINGULAR_UNITS[nUnit] : nUnit);
    }
    status.neglectWarnings = {
      complete: !!config.neglectWarnings,
      summary: neglectSummary,
    };

    var columnCount =
      (Array.isArray(config.historyCustomColumns) ? config.historyCustomColumns.length : 0) +
      (Array.isArray(config.scheduledCustomColumns) ? config.scheduledCustomColumns.length : 0);
    status.columns = {
      complete: columnCount > 0,
      summary: columnCount > 0
        ? this.props.t('inspect:sectionColumnsSummaryCount', { count: columnCount })
        : this.props.t('inspect:sectionColumnsDefault'),
    };

    var notifs = config.notifications || {};
    var totalAlerts = 0;
    var notifGroups = ['onPass', 'onFail', 'scheduledBefore', 'scheduledAfter', 'onNeglect', 'summary'];
    for (var g = 0; g < notifGroups.length; g++) {
      var arr = notifs[notifGroups[g]];
      if (arr && arr.length) totalAlerts += arr.length;
    }
    status.notifications = {
      complete: totalAlerts > 0,
      summary: totalAlerts > 0
        ? this.props.t('inspect:sectionNotificationsSummaryCount', { count: totalAlerts })
        : '',
    };

    return status;
  }.bind(this);

  render() {
    var props = this.props;
    var state = this.state;
    var t = props.t;
    var settingsEntity = props.settingsEntity;
    var isLoading = !this.isNewMode && settingsEntity && settingsEntity.isFetching;

    if (isLoading) {
      return (
        <div className="inner-page">
          <Preloader />
        </div>
      );
    }

    var ActiveSectionComponent = SECTION_COMPONENTS[state.activeSection];
    var sectionStatus = this.getSectionStatus();
    var dataErrors = state.dataErrors;
    var sectionErrorMap = {};
    var sectionErrors = null;
    SECTION_ORDER.forEach(function (key) {
      var errs = pickSectionErrors(dataErrors, key);
      sectionErrorMap[key] = !!errs;
      if (key === state.activeSection) sectionErrors = errs;
    });
    var nameError = dataErrors && dataErrors.name;
    var saveConflict = props.saveEntity && props.saveEntity.hasErrors && props.saveEntity.hasErrors.error409;
    // Creating has no Reload; its banner shows the same text as its dialog.
    if (saveConflict && this.isNewMode) saveConflict = t('inspect:saveConflictMessageNew');

    var pageTitle = this.isNewMode
      ? (state.config.name || t('inspect:newInspection'))
      : (state.config.name || t('inspect:editInspection'));

    var sectionProps = {
      config: state.config,
      onChange: this.handleConfigChange,
      settingsFields: props.settingsFields || [],
      settingsOperators: props.settingsOperators || {},
      fetchSettingsOperators: props.fetchSettingsOperators,
      onRecordsFieldChange: this.handleRecordsFieldChange,
      onAssetTypeFieldChange: this.handleAssetTypeFieldChange,
      onAssetTypeValueChange: this.handleAssetTypeValueChange,
      onActiveFieldChange: this.handleActiveFieldChange,
      onSectionNavigate: this.handleSectionClick,
      errors: sectionErrors,
      scheduleStatus: state.scheduleStatus,
      recomputeExistingDueDates: state.recomputeExistingDueDates,
      onRecomputeChange: this.handleRecomputeChange,
    };

    return (
      <div className="inner-page inspect-settings-page">
        <div className="inner-page-header">
          <h1 className="settings-header">{pageTitle}</h1>
        </div>
        <div className="inspect-settings">
          <InspectSettingsNav
            activeSection={state.activeSection}
            onSectionClick={this.handleSectionClick}
            isNewMode={this.isNewMode}
            sectionStatus={sectionStatus}
            sectionErrors={sectionErrorMap}
          />
          <div className="inspect-settings-content">
            {nameError && (
              <div className="inspect-form-error-banner">{nameError}</div>
            )}
            {saveConflict && (
              <div className="inspect-form-error-banner">{saveConflict}</div>
            )}
            {ActiveSectionComponent && (
              <ActiveSectionComponent {...sectionProps} />
            )}
          </div>
        </div>
        <IntoFooter>
          <div className="settings-footer">
            <div className="settings-footer-buttons">
              <BlueButton
                className="settings-action-cancel-btn"
                variant="secondary"
                label={t('btn:cancel')}
                onClick={this.handleCancel}
              />
              <BlueButton
                label={t('btn:save')}
                onClick={this.handleSave}
                disabled={!!(this.props.saveEntity && this.props.saveEntity.isFetching)}
              />
            </div>
          </div>
        </IntoFooter>
        <BasicDialog
          open={state.isLeaveDialogOpen}
          onClose={this.handleLeaveCancel}
          title={t('inspect:unsavedChangesTitle')}
          maxWidth="xs"
          actions={[
            <RaisedButton
              key="keep"
              label={t('inspect:keepEditing')}
              onClick={this.handleLeaveCancel}
              labelColor={palette.defaultText}
              style={{ marginRight: 10, border: '1px solid ' + palette.border }}
            />,
            <RaisedButton
              key="discard"
              label={t('inspect:discardChanges')}
              backgroundColor={palette.brandOrange}
              labelColor={palette.white}
              onClick={this.handleLeaveConfirm}
            />,
          ]}
        >
          <div style={{ padding: '20px 24px', textAlign: 'left' }}>
            {t('inspect:unsavedChangesMessage')}
          </div>
        </BasicDialog>
        {state.confirmSummary !== null && (
          <SlateDialog
            open
            onClose={this.handleConfirmBack}
            title={t('inspect:confirmRecurrenceTitle')}
            maxWidth="sm"
            actions={[
              { key: 'back', label: t('inspect:confirmRecurrenceBack'), onClick: this.handleConfirmBack },
              { key: 'save', label: t('inspect:confirmRecurrenceSave'), variant: 'primary', onClick: this.handleConfirmSave },
            ]}
          >
            <div className="inspect-confirm-summary">
              <div className="inspect-confirm-row">
                <span className="material-symbols-outlined inspect-confirm-icon inspect-confirm-icon-primary">calendar_month</span>
                <div>
                  <h3 className="inspect-confirm-headline">{state.confirmSummary.headline}</h3>
                  {state.confirmSummary.subline && <p className="inspect-confirm-subline">{state.confirmSummary.subline}</p>}
                </div>
              </div>
              {state.confirmSummary.rows.map(function (row) {
                return (
                  <div className="inspect-confirm-row" key={row.key}>
                    <span className="material-symbols-outlined inspect-confirm-icon">{row.icon}</span>
                    <p className="inspect-confirm-text">{row.text}</p>
                  </div>
                );
              })}
              {state.confirmSummary.onSave && (
                <div className="inspect-confirm-row inspect-due-banner inspect-confirm-onsave">
                  <span className="material-symbols-outlined inspect-confirm-icon">sync</span>
                  <div>
                    <h4 className="inspect-confirm-onsave-title">{state.confirmSummary.onSave.title}</h4>
                    <p className="inspect-confirm-text">{state.confirmSummary.onSave.text}</p>
                  </div>
                </div>
              )}
            </div>
          </SlateDialog>
        )}
        {state.isConflictDialogOpen && (
          <SlateDialog
            open
            onClose={this.handleConflictCancel}
            title={t('inspect:saveConflictTitle')}
            maxWidth="xs"
            actions={this.isNewMode
              ? [
                { key: 'keep', label: t('inspect:keepEditing'), variant: 'primary', onClick: this.handleConflictCancel },
              ]
              : [
                { key: 'cancel', label: t('btn:cancel'), onClick: this.handleConflictCancel },
                { key: 'reload', label: t('inspect:saveConflictReload'), variant: 'destructive', onClick: this.handleConflictReload },
              ]}
          >
            <p style={{ margin: 0, color: 'var(--sl-card-title)', fontSize: 14 }}>
              {this.isNewMode ? t('inspect:saveConflictMessageNew') : t('inspect:saveConflictMessage')}
            </p>
          </SlateDialog>
        )}
      </div>
    );
  }
}

var mapStateToProps = function (state) {
  return {
    currentAccount: state.accounts.currentAccount,
    inspectionSettings: state.inspect.inspectionSettings,
    inspectionSettingsRaw: state.inspect.inspectionSettingsRaw,
    settingsEntity: state.entities[entityNames.GET_INSPECTION],
    saveEntity: state.entities[entityNames.UPDATE_INSPECTION],
    settingsFields: state.slateViews.settingsFields,
    settingsOperators: state.slateViews.settingsOperators,
  };
};

var mapDispatchToProps = {
  fetchInspectionSettings: fetchInspectionSettings,
  clearInspectionSettings: clearInspectionSettings,
  createInspection: createInspection,
  updateInspection: updateInspection,
  fetchSettingsFields: fetchSettingsFields,
  fetchSettingsOperators: fetchSettingsOperators,
  fetchReset: fetchReset,
};

export default withTranslation()(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(InspectSettings)),
);
